
/**
 * Add early hints for faster resource loading
 */
export function addResourceHintsForFasterLoading() {
  // Skip if not in browser
  if (typeof document === 'undefined') return;
  
  console.log('[ResourceHints] Initializing resource hints');
  
  // Preconnect to important domains
  const preconnectDomains = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
    'https://storage.googleapis.com',
    'https://ginefbvgxcobyuocxerb.supabase.co' // Add Supabase URL
  ];
  
  preconnectDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    
    // Only add if not already present
    if (!document.querySelector(`link[rel="preconnect"][href="${domain}"]`)) {
      document.head.appendChild(link);
      console.log(`[ResourceHints] Added preconnect for ${domain}`);
    } else {
      console.log(`[ResourceHints] Preconnect for ${domain} already exists`);
    }
  });
  
  // Define critical images with responsive formats 
  const criticalImages = [
    {
      // Updated to use storage bucket pattern with correct file extension
      baseUrl: 'https://ginefbvgxcobyuocxerb.supabase.co/storage/v1/object/public/logos/fish-header.png',
      sizes: '40px',
      formats: ['avif', 'webp', 'png'],
      widths: [40, 80] // Small sizes for the header logo
    },
    {
      baseUrl: 'https://ginefbvgxcobyuocxerb.supabase.co/storage/v1/object/public/logos/fish-footer.png',
      sizes: '128px',
      formats: ['avif', 'webp', 'png'],
      widths: [128, 256] // Larger sizes for the footer logo
    }
  ];
  
  // Check for supported formats
  const checkFormatSupport = async () => {
    // Default to most compatible format
    let bestFormat = 'png';
    
    // Check WebP support with proper Promise chain
    if ('createImageBitmap' in window) {
      try {
        const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
        const blob = await fetch(webpData).then(r => r.blob());
        await createImageBitmap(blob);
        bestFormat = 'webp';
        console.log('[ResourceHints] WebP format is supported');
        
        // Check AVIF support (optional, more advanced)
        try {
          const avifData = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';
          const avifBlob = await fetch(avifData).then(r => r.blob());
          await createImageBitmap(avifBlob);
          bestFormat = 'avif';
          console.log('[ResourceHints] AVIF format is supported');
        } catch (avifError) {
          console.log('[ResourceHints] AVIF not supported, using WebP instead');
        }
      } catch (webpError) {
        console.log('[ResourceHints] WebP not supported, using PNG fallback');
      }
    } else {
      console.log('[ResourceHints] createImageBitmap not supported, using PNG fallback');
    }
    
    // Return the best format (async detection will update later)
    return bestFormat;
  };
  
  // Get initial format and preload images
  checkFormatSupport().then(initialFormat => {
    console.log(`[ResourceHints] Using ${initialFormat} as the preferred image format`);
    
    // Log full logo paths to be preloaded
    criticalImages.forEach(image => {
      console.log(`[ResourceHints] Will attempt to preload: ${image.baseUrl}`);
    });
    
    // Preload critical logo with the appropriate format and size
    criticalImages.forEach(image => {
      // Only preload the primary logo for immediate display
      if (image.baseUrl.includes('fish-header')) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = image.baseUrl;
        link.setAttribute('fetchpriority', 'high');
        link.crossOrigin = 'anonymous';
        
        // Only add if not already present
        if (!document.querySelector(`link[rel="preload"][href="${link.href}"]`)) {
          document.head.appendChild(link);
          console.log(`[ResourceHints] Preloaded image: ${link.href}`);
        } else {
          console.log(`[ResourceHints] Image already preloaded: ${link.href}`);
        }
      }
    });
  }).catch(error => {
    console.error('[ResourceHints] Error checking format support:', error);
  });
  
  // Preload main JavaScript
  const mainScripts = document.querySelectorAll('script[src]');
  mainScripts.forEach(script => {
    const src = script.getAttribute('src');
    if (src && src.includes('main') && !src.includes('vendor')) {
      const link = document.createElement('link');
      link.rel = 'modulepreload';
      link.href = src;
      
      // Only add if not already present
      if (!document.querySelector(`link[rel="modulepreload"][href="${src}"]`)) {
        document.head.appendChild(link);
        console.log(`[ResourceHints] Preloaded script: ${src}`);
      } else {
        console.log(`[ResourceHints] Script already preloaded: ${src}`);
      }
    }
  });
  
  // Add DNS prefetch for additional domains
  const dnsPrefetchDomains = [
    'https://images.unsplash.com'
  ];
  
  dnsPrefetchDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = domain;
    
    // Only add if not already present
    if (!document.querySelector(`link[rel="dns-prefetch"][href="${domain}"]`)) {
      document.head.appendChild(link);
      console.log(`[ResourceHints] Added DNS prefetch for ${domain}`);
    } else {
      console.log(`[ResourceHints] DNS prefetch for ${domain} already exists`);
    }
  });

  console.log('[ResourceHints] All resource hints have been added');
}

/**
 * Initialize resource hints immediately
 */
export function initResourceHints() {
  // Add initial hints as soon as possible
  if (typeof document !== 'undefined') {
    console.log('[ResourceHints] Starting initialization');
    // Use requestIdleCallback if available, otherwise use setTimeout
    if ('requestIdleCallback' in window) {
      console.log('[ResourceHints] Using requestIdleCallback for initialization');
      (window as any).requestIdleCallback(() => {
        console.time('[ResourceHints] Execution Time');
        addResourceHintsForFasterLoading();
        console.timeEnd('[ResourceHints] Execution Time');
      });
    } else {
      console.log('[ResourceHints] Using setTimeout fallback for initialization');
      setTimeout(() => {
        console.time('[ResourceHints] Execution Time');
        addResourceHintsForFasterLoading();
        console.timeEnd('[ResourceHints] Execution Time');
      }, 0);
    }
  } else {
    console.log('[ResourceHints] Document not available, skipping initialization');
  }
}

// Run immediately
initResourceHints();
